import {Heading, Link, IconButton, Truncate} from '@primer/react'
import {VerifiedIcon, PackageIcon, RepoIcon, LockIcon, CopyIcon, CheckIcon} from '@primer/octicons-react'
import {repositoryPath} from '@github-ui/paths'
import {useState} from 'react'
import styles from './SourceRepo.module.css'
import type {RegistryAttestation} from '../types'

interface SourceRepoProps {
  attestation?: RegistryAttestation
  repositoryName?: string
  org: string
  repository_is_private?: boolean
  digest?: string
}

const iconProps = {size: 16, className: 'fgColor-muted'}

function getRepositoryIcon(isPrivate?: boolean) {
  return isPrivate ? (
    <LockIcon {...iconProps} data-testid="private-repo-icon" />
  ) : (
    <RepoIcon {...iconProps} data-testid="public-repo-icon" />
  )
}

export function SourceRepo({attestation, repositoryName, org, repository_is_private, digest}: SourceRepoProps) {
  const hasAttestation = !!attestation && !!repositoryName
  const [isCopied, setIsCopied] = useState(false)
  const repositoryDisplay = repositoryName ? (
    <Link
      href={repositoryPath({owner: org, repo: repositoryName})}
      data-hovercard-url={`${repositoryPath({owner: org, repo: repositoryName})}/hovercard`}
      inline
      className={styles.Link}
      suppressHydrationWarning
    >
      {repositoryName}
    </Link>
  ) : null

  const handleCopyDigest = async () => {
    if (digest) {
      try {
        await navigator.clipboard.writeText(digest)
        setIsCopied(true)
        setTimeout(() => setIsCopied(false), 2000)
      } catch {
        // Silently fail if clipboard access is denied
      }
    }
  }

  return (
    <aside>
      <Heading as="h3" className={styles.Header}>
        Source repository
      </Heading>

      <section className={styles.Container}>
        <InfoRow label="Repository" icon={getRepositoryIcon(repository_is_private)}>
          {repositoryDisplay}
        </InfoRow>

        <InfoRow
          label="Method"
          icon={
            hasAttestation ? <VerifiedIcon size={16} className="fgColor-success" /> : <PackageIcon {...iconProps} />
          }
        >
          {hasAttestation ? (
            <Link href={`/${org}/${repositoryName}/attestations/${attestation.id}`} inline className={styles.Link}>
              Provenance attestation
            </Link>
          ) : (
            <span>artifact-metadata-api</span>
          )}
        </InfoRow>

        {hasAttestation && attestation.runInvocationUri && attestation.workflowFile && (
          <InfoRow label="Workflow">
            <Truncate title={attestation.workflowFile} maxWidth="25ch" inline>
              <Link href={attestation.runInvocationUri} inline className={styles.Link}>
                {attestation.workflowFile}
              </Link>
            </Truncate>
          </InfoRow>
        )}

        <InfoRow label="SLSA level">{hasAttestation ? '2+' : '0 (Non-compliant)'}</InfoRow>
      </section>

      {!hasAttestation && (
        <Link
          href="https://docs.github.com/en/actions/how-tos/secure-your-work/use-artifact-attestations/use-artifact-attestations"
          className={styles.DocLink}
        >
          Learn more about provenance attestations to improve SLSA levels in future artifact versions
        </Link>
      )}

      {digest && (
        <>
          <hr className={styles.Divider} />
          <div>
            <div className={styles.DigestLabel}>Digest</div>
            <div className={styles.DigestInline}>
              <div className={styles.DigestValue}>
                <Truncate title={digest} maxWidth={`100%`}>
                  {digest}
                </Truncate>
              </div>
              <IconButton
                aria-label={isCopied ? 'Copied!' : 'Copy digest'}
                icon={isCopied ? CheckIcon : CopyIcon}
                size="small"
                variant="invisible"
                onClick={handleCopyDigest}
              />
            </div>
          </div>
        </>
      )}
    </aside>
  )
}

function InfoRow({label, icon, children}: {label: string; icon?: React.ReactElement; children: React.ReactNode}) {
  return (
    <div className={styles.Row}>
      <span className={styles.Label}>{label}</span>
      <span className={`${styles.Value} ${icon ? styles.ValueWithIcon : ''}`}>
        {icon}
        {children}
      </span>
    </div>
  )
}
